// One flat content list's reorder (plan R6 §2.1): the row moves on screen at
// once, then PUT <url> { ...scope, ids, expectedIds } saves the whole scope.
// The answer's fresh list replaces the screen; a failure puts the pre-move
// order back and shows a sentence right above the list, and a 409
// ContentOrderChanged also reloads the list quietly. One move at a time.
import { useCallback, useRef, useState } from 'react';
import axiosInstance from 'src/utils/axios';
import {
  Identified,
  MoveKind,
  isOrderChanged,
  itemsFromAnswer,
  moveBy,
  orderErrorText,
  reorderBody,
  rollback,
} from './reorderModel';

type Options<T> = {
  // PUT route, e.g. APIS.HOME_STATS.REORDER.
  url: string;
  // Puts a scope (in its new order) on screen; the page merges it into its rows.
  apply: (scopeRows: T[]) => void;
  // Reloads the whole list (after a 409 ContentOrderChanged).
  reload: () => Promise<unknown> | void;
};

export default function useContentReorder<T extends Identified>({ url, apply, reload }: Options<T>) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = useRef(false);

  const move = useCallback(
    async (scopeRows: T[], index: number, kind: MoveKind, scope: Record<string, unknown> = {}) => {
      if (running.current) return false;
      const before = [...scopeRows];
      const after = moveBy(before, index, kind);
      if (after.every((r, i) => r === before[i])) return false;
      running.current = true;
      setSaving(true);
      setError(null);
      apply(after);
      try {
        const res = await axiosInstance.put(url, reorderBody(before, after, scope));
        apply(itemsFromAnswer<T>(res) ?? after);
        return true;
      } catch (err) {
        apply(rollback(before));
        setError(orderErrorText(err));
        if (isOrderChanged(err)) {
          try {
            await reload();
          } catch {
            // the reload shows its own error
          }
        }
        return false;
      } finally {
        running.current = false;
        setSaving(false);
      }
    },
    [url, apply, reload]
  );

  return { move, saving, error, clearError: () => setError(null) };
}
